import {
    useCallback,
    useMemo,
    useState,
    type CSSProperties,
    type FormEvent,
    type ReactElement,
} from "react";
import { Plus, X } from "lucide-react";
import { space } from "@tokens/index";
import { Button, IconButton, TextInput, useViewport } from "@primitives/index";
import { AdminSection, AdminSelect, EmptyLine, FormActions, MutationError } from "../admin-ui";
import { useSetManagementGroupScopes } from "../management-groups";
import type { ManagementGroup, WorkflowOwnershipScope } from "../management-groups";
import { useWorkflowKinds } from "../workflows";

/**
 * Owned workflow scopes. Each row is a kind plus an optional entity type.
 * `medium`+ places kind, entity type, and the remove control on one line;
 * `compact` stacks kind over entity type with the remove control trailing.
 */

interface ScopeDraft {
    readonly id: string;
    readonly kind: string;
    readonly entityType: string;
}

function scopesToDraft(scopes: readonly WorkflowOwnershipScope[]): ScopeDraft[] {
    return scopes.map((s) => ({
        id: crypto.randomUUID(),
        kind: s.kind,
        entityType: s.entityType ?? "",
    }));
}

const wideRowStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
    gap: space[2],
    alignItems: "center",
};

const compactRowStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: space[2],
    alignItems: "start",
};

export function ScopesSection({ group }: { readonly group: ManagementGroup }): ReactElement {
    const { isCompact } = useViewport();
    const kindsQuery = useWorkflowKinds();
    const save = useSetManagementGroupScopes();
    const [drafts, setDrafts] = useState<ScopeDraft[]>(() =>
        scopesToDraft(group.ownedWorkflowScopes),
    );
    const [dirty, setDirty] = useState(false);

    // useMemo keeps `kinds` referentially stable across renders so the
    // callbacks below don't churn dependency arrays.
    const kinds = useMemo(() => kindsQuery.data?.kinds ?? [], [kindsQuery.data]);

    const handleAdd = useCallback(() => {
        const used = new Set(drafts.map((d) => `${d.kind}:${d.entityType}`));
        const firstUnusedKind = kinds.find((k) => !used.has(`${k}:`)) ?? kinds[0] ?? "";
        setDrafts((prev) => [
            ...prev,
            { id: crypto.randomUUID(), kind: firstUnusedKind, entityType: "" },
        ]);
        setDirty(true);
    }, [drafts, kinds]);

    const handleUpdate = useCallback((id: string, patch: Partial<Omit<ScopeDraft, "id">>) => {
        setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
        setDirty(true);
    }, []);

    const handleRemove = useCallback((id: string) => {
        setDrafts((prev) => prev.filter((d) => d.id !== id));
        setDirty(true);
    }, []);

    const handleReset = useCallback(() => {
        setDrafts(scopesToDraft(group.ownedWorkflowScopes));
        setDirty(false);
    }, [group.ownedWorkflowScopes]);

    const handleSave = useCallback(() => {
        save.mutate(
            {
                id: group.id,
                scopes: drafts.map((d) => ({
                    kind: d.kind,
                    entityType: d.entityType.trim().length === 0 ? null : d.entityType.trim(),
                })),
            },
            {
                onSuccess: () => {
                    setDirty(false);
                },
            },
        );
    }, [save, group.id, drafts]);

    // Enter in any row saves, through the form, when there is something to save.
    const handleSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (!dirty || save.isPending) return;
            handleSave();
        },
        [dirty, save.isPending, handleSave],
    );

    const addButton = (
        <Button type="button" variant="ghost" size="sm" onClick={handleAdd}>
            <Plus size={14} strokeWidth={2.25} aria-hidden="true" />
            Add scope
        </Button>
    );

    return (
        <AdminSection
            title="Owned workflow scopes"
            count={drafts.length}
            description="Workflows matching any scope below roll up to this group for reporting. Leave entity type blank to claim the whole kind."
        >
            <form onSubmit={handleSubmit} style={{ display: "contents" }}>
            {drafts.length === 0 && (
                <EmptyLine action={addButton}>No scopes — this group owns nothing yet.</EmptyLine>
            )}
            {drafts.map((d) => {
                const kindField = (
                    <AdminSelect
                        aria-label="Workflow kind"
                        value={d.kind}
                        onChange={(e) => handleUpdate(d.id, { kind: e.target.value })}
                    >
                        {kinds.map((k) => (
                            <option key={k} value={k}>
                                {k}
                            </option>
                        ))}
                    </AdminSelect>
                );
                const entityField = (
                    <TextInput
                        aria-label="Entity type"
                        placeholder="Entity type (blank = all)"
                        fullWidth
                        value={d.entityType}
                        onChange={(e) => handleUpdate(d.id, { entityType: e.target.value })}
                    />
                );
                const removeButton = (
                    <IconButton
                        type="button"
                        icon={X}
                        aria-label="Remove scope"
                        tooltip="Remove scope"
                        onClick={() => handleRemove(d.id)}
                    />
                );
                return (
                    <div key={d.id} style={isCompact ? compactRowStyle : wideRowStyle}>
                        {isCompact ? (
                            <>
                                <div style={{ display: "grid", gap: space[2], minWidth: 0 }}>
                                    {kindField}
                                    {entityField}
                                </div>
                                {removeButton}
                            </>
                        ) : (
                            <>
                                {kindField}
                                {entityField}
                                {removeButton}
                            </>
                        )}
                    </div>
                );
            })}

            {drafts.length > 0 && <div>{addButton}</div>}

            <FormActions
                primary={
                    <Button
                        type="submit"
                        variant="primary"
                        size="md"
                        disabled={!dirty}
                        busy={save.isPending}
                    >
                        Save
                    </Button>
                }
                secondary={
                    dirty ? (
                        <Button
                            type="button"
                            variant="ghost"
                            size="md"
                            onClick={handleReset}
                            disabled={save.isPending}
                        >
                            Reset
                        </Button>
                    ) : undefined
                }
            />

            <MutationError error={save.error} />
            </form>
        </AdminSection>
    );
}
